/**
 * AR projection utilities — convert bearing/distance/distance to screen
 * overlay positions for the pseudo-AR camera view.
 */

/** Camera horizontal field of view in degrees (typical phone rear camera). */
export const CAMERA_HFOV = 65;

/** Maximum distance (metres) for assets to appear as overlay cards. */
export const AR_MAX_DISTANCE = 100;

/** Distance bands for vertical positioning (closer = lower on screen). */
const DISTANCE_BANDS = [
  { max: 20, y: 0.75 },   // very close — bottom of screen
  { max: 40, y: 0.65 },
  { max: 60, y: 0.55 },
  { max: 80, y: 0.48 },
  { max: 100, y: 0.42 }   // far — upper area
];

export interface ProjectedAsset {
  /** Screen X as fraction of viewport width (0-1), null if off-screen. */
  screenX: number | null;
  /** Screen Y as fraction of viewport height (0-1). */
  screenY: number;
  /** Compass bearing from user to asset (0-359). */
  bearing: number;
  /** Distance in metres. */
  distance: number;
  /** Whether the asset is within the camera FOV. */
  inView: boolean;
  /** Horizontal offset direction for off-screen indicator (-1 = left, 1 = right). */
  offscreenDirection: 0 | -1 | 1;
}

/**
 * Project an asset's bearing and distance into screen space.
 * @param assetBearing compass bearing from user to asset (0-359, 0=N)
 * @param userHeading compass heading the user is facing (0-359, 0=N)
 * @param distance distance to asset in metres
 * @returns projected position info
 */
export function projectAsset(
  assetBearing: number,
  userHeading: number,
  distance: number
): ProjectedAsset {
  // Relative angle: how far the asset is from the center of the view
  let relAngle = assetBearing - userHeading;
  // Normalize to -180..180
  while (relAngle > 180) relAngle -= 360;
  while (relAngle < -180) relAngle += 360;

  const halfFov = CAMERA_HFOV / 2;
  const inView = Math.abs(relAngle) <= halfFov;

  let screenX: number | null;
  let offscreenDirection: 0 | -1 | 1 = 0;

  if (inView) {
    // Map relative angle to screen X: -halfFov → 0, 0 → 0.5, +halfFov → 1
    screenX = 0.5 + relAngle / CAMERA_HFOV;
  } else {
    screenX = null;
    offscreenDirection = relAngle < 0 ? -1 : 1;
  }

  // Vertical position from distance band
  const band = DISTANCE_BANDS.find((b) => distance <= b.max) ?? DISTANCE_BANDS[DISTANCE_BANDS.length - 1]!;
  const screenY = band.y;

  return {
    screenX,
    screenY,
    bearing: assetBearing,
    distance,
    inView,
    offscreenDirection
  };
}

/** Compass direction label from a bearing in degrees. */
export function bearingLabel(bearing: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
  const idx = Math.round(bearing / 45) % 8;
  return dirs[idx]!;
}
